import React, { useEffect, useState } from 'react'
import { ProjectProps } from '../Project'
import MainBlock from '../../../components/MainBlock'
import { Title } from '../../../components/General'
import { useTranslation } from 'react-i18next'
import Loading from '../../../components/Loading'
import { getStandardTime, useAsyncEffect, useEventListener } from '../../../utils/functions'
import { FunctionModel } from '../../../utils/interfaces'
import { Admin, createItem, deleteItems, getItems } from '../../../api/API'
import { freeLimits, proLimits, tables } from '../../../utils/constants'
import Table from '../../../components/Table'
import { useNavigate } from 'react-router-dom'
import AlertWrapper from '../../../components/AlertWrapper'
import Alert from '../../../components/Alert'
import { Input } from '../../../components/Input'
import { getNewFunctionId, starterCodes } from './FunctionUtils'
import { TbExternalLink } from 'react-icons/tb'

const Function = (props: ProjectProps) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [functions, setFunctions] = useState<FunctionModel[]>([]);
  const [funcInfos, setFuncInfos] = useState<Record<string, any>[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [showDeleteFunctionsAlert, setShowDeleteFunctionsAlert] = useState(false);
  const [showAddFunctionAlert, setShowAddFunctionAlert] = useState(false);
  // new function
  const [newFunctionName, setNewFunctionName] = useState('');
  const [template, setTemplate] = useState<'' | 'signIn' | 'getItem'>('');
  
  // event handlers
  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.add-function-button') && !target.closest('.add-function-alert')) {
      setShowAddFunctionAlert(false);
    }
    if (!target.closest('.delete-functions-button') && !target.closest('.delete-functions-alert')) {
      setShowDeleteFunctionsAlert(false);
    }
  })

  useEffect(() => {
    setNewFunctionName('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddFunctionAlert]);

  // get function infos
  useEffect(() => {
    let filtered: Record<string, any>[] = [];
    for (const f of functions) {
      filtered.push({ ID: f.id, [t('table-attr.general.name')]: f.name, [t('table-attr.general.last-update')]: getStandardTime(new Date(f.lastUpdate)) });
    }
    setFuncInfos(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [functions]);

  // load functions
  useAsyncEffect(async () => {
    const data = (await getItems(tables.functions, { projectId: props.project.id })).items;
    if (data) {
      setFunctions(data);
    }
    setIsLoading(false);
  }, []);

  // check limit
  const hasReachedLimit = () => {
    return props.user?.plan.name === 'pro' ? functions.length >= proLimits.functions
      : props.user?.plan.name === 'free' ? functions.length >= freeLimits.functions
        : false;
  }

  // add new function
  const addNewFunction = async () => {
    setIsAdding(true);
    const code = template ? starterCodes[template] : starterCodes.default;
    const newFunction: FunctionModel = { id: getNewFunctionId(), name: newFunctionName, code, language: 'javascript', autoSave: true, userId: props.user!.id, projectId: props.project.id, description: '', adminIds: [{ userId: props.user!.id, access: 'full_access' }], createdAt: new Date().toISOString(), lastUpdate: new Date().toISOString() };
    await createItem(tables.functions, newFunction);
    // create env if choose template
    if (template) {
      await Admin.setupDefaultFunctionEnvs(props.project.id, newFunction.id);
    }
    setFunctions(prev => [...prev, newFunction]);
    setShowAddFunctionAlert(false);
    setIsAdding(false);
    navigate(`/project/${props.project.id}/function/${newFunction.id}`);
  }

  // delete functions
  const deleteFunctions = async () => {
    setIsDeleting(true);
    await deleteItems(tables.functions, selected);
    setFunctions(functions.filter(f => !selected.includes(f.id)));
    setSelected([]);
    setIsDeleting(false);
    setShowDeleteFunctionsAlert(false);
  }


  return (
    isLoading ? <main className='flex items-center justify-center flex-1'><Loading solid size={8} /></main>
      : <>
        <div className='flex-1 flex flex-col p-4 md:px-8 overflow-auto'>
          <div className='flex items-center justify-between gap-4'>
            <Title>{t('sidebar-menu.project.function')}</Title>
          </div>
          <div className='flex-1 overflow-auto mt-4 flex flex-col'>
            <MainBlock extraclass='flex-1 flex flex-col overflow-auto'>
              <div className='flex items-center justify-between'>
                <div>
                  <div className='text-sm md:text-base font-bold'>{t('project.function.all-functions')} ({funcInfos.length})</div>
                </div>
                <div className='gap-4 flex my-1 flex-row'>
                  {funcInfos.length > 0 && <button onClick={() => setShowDeleteFunctionsAlert(true)} disabled={selected.length === 0} className='delete-functions-button bg-gray-100 disabled:opacity-30 rounded-lg p-2 px-4 text-xs'>{t('project.function.delete-functions')}</button>}
                  <button onClick={() => setShowAddFunctionAlert(true)} className='add-function-button bg-primary text-white rounded-lg p-2 px-4 text-xs'>{t('project.function.add-function')}</button>
                </div>
              </div>
              <div className='flex flex-col flex-1 overflow-auto mt-2'>
                <Table
                  data={funcInfos}
                  setData={setFuncInfos}
                  selected={selected}
                  setSelected={setSelected}
                  attrKey={'ID'}
                  isKeyBotton
                  onClick={(e, item) => navigate(`/project/${props.project.id}/function/${item['ID']}`)}
                  attributes={[{ name: 'ID', type: 'string' }, { name: t('table-attr.general.name'), type: 'string' }, { name: t('table-attr.general.last-update'), type: 'string' }]}
                />
                {funcInfos.length === 0 && <div className='flex-1 flex items-center justify-center font-light text-sm'>{t('project.function.no-function')}</div>}
              </div>
            </MainBlock>
          </div>
        </div>


        {/* delete functions */}
        {showDeleteFunctionsAlert && <AlertWrapper>
          <Alert
            extraclass='delete-functions-alert'
            title={t('project.function.delete-functions')}
            maxWidth={480}
            onClose={() => setShowDeleteFunctionsAlert(false)}
            body={<div className='mx-1'>
              <div>{t('project.function.delete-function-message')}</div>
            </div>}
            bottom={<div className='flex'>
              <button onClick={deleteFunctions} disabled={selected.length === 0 || isDeleting} className='disabled:bg-gray-300 bg-primary text-white text-sm p-2 px-8 rounded-lg'>{isDeleting ? <Loading /> : t('general.delete')}</button>
            </div>}
          />
        </AlertWrapper>}

        {/* add function */}
        {showAddFunctionAlert && <AlertWrapper>
          <Alert
            extraclass='add-function-alert'
            title={t('project.function.add-function')}
            maxWidth={400}
            onClose={() => setShowAddFunctionAlert(false)}
            body={<div className='flex flex-col'>
              <div>
                <label className='text-xs text-gray-400 m-1'>{t('general.name')}</label>
                <Input autoFocus value={newFunctionName} onChange={(e) => setNewFunctionName(e.target.value)} />
                {functions.find(p => p.name === newFunctionName) && <p className='text-xs text-red-400 m-1'>{t('home.console.project.new-project.name-exists')}</p>}
              </div>
              <div className='mt-4'>
                <label className='text-xs text-gray-400 m-1'>{t('project.function.add-function.use-template')}</label>
                <div className='flex flex-col gap-2'>
                  <button onClick={() => {
                    setTemplate(template === 'signIn' ? '' : 'signIn');
                    setNewFunctionName(template === 'signIn' ? '' : 'Sign in');
                  }} className={`animate flex flex-col p-2 rounded-lg border ${template === 'signIn' ? 'bg-primary/10 border-primary' : ''}`}><span className='font-bold text-xs'>Sign in</span><span className='text-xs text-gray-400 font-light whitespace-pre-wrap text-start'>{t('project.function.add-function.use-template.sign-in')}</span></button>
                  <button onClick={() => {
                    setTemplate(template === 'getItem' ? '' : 'getItem');
                    setNewFunctionName(template === 'getItem' ? '' : 'Get item');
                  }} className={`animate flex flex-col p-2 rounded-lg border ${template === 'getItem' ? 'bg-primary/10 border-primary' : ''}`}><span className='font-bold text-xs'>Get item</span><span className='text-xs text-gray-400 font-light whitespace-pre-wrap text-start'>{t('project.function.add-function.use-template.get-item')}</span></button>
                </div>
              </div>
            </div>}
            bottom={<div>
              {hasReachedLimit() && <div className='text-xs m-1 text-red-400'>{t('project.function.add-function.limit')}</div>}
              <div className='flex'><button onClick={addNewFunction} disabled={hasReachedLimit() || isAdding || !newFunctionName || !!functions.find(p => p.name === newFunctionName)} className='flex items-center justify-center bg-primary disabled:bg-gray-300 text-white px-8 py-2 rounded-lg text-sm'>{isAdding ? <Loading /> : t('general.add')}</button></div>
            </div>}
          />
        </AlertWrapper>}
      </>
  )
}

export default Function